import React, { useMemo } from 'react';
import {
  buildCajasDisplayRows,
  buildCajasTableColumns,
  buildDesgloseDisplayRows,
  cajaRowCellValue,
  formatSoloTotalesDate,
  formatSoloTotalesMoney,
  RELACION_VENTAS_SOLO_TOTALES_TITULO,
} from '../../../utils/relacionVentasSoloTotalesModel';
import { encabezadoTieneDatos } from '../../../utils/reporteEncabezadoApi';
import '../PopularidadReportLayout/PopularidadReportLayout.css';
import './RelacionVentasSoloTotalesLayout.css';

function ResumenLine({ label, value, negative = false, strong = false }) {
  const cls = [
    'rv-st-line',
    negative ? 'rv-st-line--negative' : '',
    strong ? 'rv-st-line--strong' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const num = Number(value);
  const display =
    negative && Number.isFinite(num) && num !== 0
      ? formatSoloTotalesMoney(-Math.abs(num))
      : formatSoloTotalesMoney(value);
  return (
    <div className={cls}>
      <span className="rv-st-line__label">{label}</span>
      <span className="rv-st-line__value">{display}</span>
    </div>
  );
}

function DesgloseTable({ titulo, grupo }) {
  const { filas, totales } = buildDesgloseDisplayRows(grupo);
  if (!filas.length) return null;

  return (
    <section className="rv-st-section">
      <h3 className="rv-st-section__title">{titulo}</h3>
      <div className="rv-st-table-wrap">
        <table className="rv-st-table">
          <thead>
            <tr>
              <th>Forma de pago</th>
              <th className="rv-st-num">Pago</th>
              <th className="rv-st-num">Propina</th>
              <th className="rv-st-num">Total</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={`${f.nombre}-${i}`}>
                <td>
                  {f.nombre}
                  {f.moneda != null && (
                    <span className="rv-st-moneda"> · {formatSoloTotalesMoney(f.moneda)} USD</span>
                  )}
                </td>
                <td className="rv-st-num">{formatSoloTotalesMoney(f.pago)}</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(f.propina)}</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(f.total)}</td>
              </tr>
            ))}
          </tbody>
          {totales && (
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(totales.pago)}</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(totales.propina)}</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(totales.total)}</td>
              </tr>
              <tr className="rv-st-foot-sub">
                <td colSpan={2}>Com. propina</td>
                <td className="rv-st-num" colSpan={2}>
                  {formatSoloTotalesMoney(totales.comPropina)}
                </td>
              </tr>
              <tr className="rv-st-foot-sub">
                <td colSpan={2}>Prop. s/com</td>
                <td className="rv-st-num" colSpan={2}>
                  {formatSoloTotalesMoney(totales.propSinCom)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

function SoloTotalesSeccionBody({ seccion, ocultarFolios = false }) {
  const cajasRows = useMemo(() => buildCajasDisplayRows(seccion?.cajas), [seccion?.cajas]);
  const cajasColumns = useMemo(() => buildCajasTableColumns(ocultarFolios), [ocultarFolios]);
  if (!seccion) return null;

  const alertas = seccion.alertas ?? {};
  const venta = seccion.venta ?? {};
  const cortesia = seccion.cortesia ?? {};
  const abo = seccion.aliBebOtr ?? {};
  const ef = seccion.efectivoFinal ?? {};

  return (
    <>
      {(alertas.hayPendientes || (!ocultarFolios && alertas.faltanCheques)) && (
        <div className="rv-st-alerts">
          {!ocultarFolios && alertas.faltanCheques && alertas.singleDateTurn && (
            <div className="rv-st-alert rv-st-alert--warn">Faltan cheques — verifique folios</div>
          )}
          {alertas.hayPendientes && (
            <div className="rv-st-alert rv-st-alert--danger">
              Hay cuentas sin saldar (se toman como efectivo)
            </div>
          )}
        </div>
      )}

      {cajasRows.length > 0 && (
        <section className="rv-st-section">
          <h3 className="rv-st-section__title">Resumen por caja</h3>
          <div className="rv-st-table-wrap">
            <table className="rv-st-table">
              <thead>
                <tr>
                  {cajasColumns.map((col) => (
                    <th
                      key={col.id}
                      className={col.align === 'num' ? 'rv-st-num' : undefined}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cajasRows.map((c) => (
                  <tr key={c.caja} className={c.hayAbiertas ? 'rv-st-row-pending' : ''}>
                    {cajasColumns.map((col) => (
                      <td
                        key={col.id}
                        className={col.align === 'num' ? 'rv-st-num' : undefined}
                      >
                        {cajaRowCellValue(c, col.id)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="rv-st-cards">
        <article className="rv-st-card">
          <h3 className="rv-st-card__title">Venta</h3>
          <ResumenLine label="Subtotal" value={venta.subtotal} />
          <ResumenLine label="Descuento" value={venta.descuento} negative />
          {Number(venta.monedero) > 0 && <ResumenLine label="Monedero" value={venta.monedero} negative />}
          <ResumenLine label="Total" value={venta.total} strong />
        </article>

        <article className="rv-st-card rv-st-card--muted">
          <h3 className="rv-st-card__title">Cortesía</h3>
          <ResumenLine label="Subtotal" value={cortesia.subtotal} />
          {Number(cortesia.descuento) > 0 && <ResumenLine label="Descuento" value={cortesia.descuento} />}
          <ResumenLine label="Total" value={cortesia.total} strong />
        </article>
      </div>

      <DesgloseTable titulo="Desglose de pagos — venta" grupo={seccion.desgloseVenta} />
      <DesgloseTable titulo="Desglose de pagos — cortesía" grupo={seccion.desgloseCortesia} />

      <section className="rv-st-section">
        <h3 className="rv-st-section__title">Alimentos / bebidas / otros</h3>
        <div className="rv-st-table-wrap">
          <table className="rv-st-table">
            <thead>
              <tr>
                <th>Concepto</th>
                <th className="rv-st-num">Venta</th>
                <th className="rv-st-num">Cheque prom.</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Alimentos</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(abo.totalAli)}</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(abo.chAli)}</td>
              </tr>
              <tr>
                <td>Bebidas</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(abo.totalBeb)}</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(abo.chBeb)}</td>
              </tr>
              <tr>
                <td>Otros</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(abo.totalOtr)}</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(abo.chOtr)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total ({abo.totalPersonas ?? 0} personas)</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(abo.total)}</td>
                <td className="rv-st-num">{formatSoloTotalesMoney(abo.chProm)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section className="rv-st-efectivo">
        <h3 className="rv-st-efectivo__title">Efectivo final</h3>
        <div className="rv-st-efectivo__grid">
          <ResumenLine label="Efectivo" value={ef.efectivo} />
          <ResumenLine label="Propinas firmadas" value={ef.propinasFirmadas} negative />
          {(Array.isArray(ef.gastos) ? ef.gastos : []).map((g, i) => (
            <ResumenLine
              key={`${g.concepto}-${i}`}
              label={String(g.concepto ?? 'Gasto')}
              value={g.importe}
              negative
            />
          ))}
        </div>
        <div className="rv-st-efectivo__total">
          <span>Efectivo final</span>
          <strong>{formatSoloTotalesMoney(ef.total)}</strong>
        </div>
      </section>
    </>
  );
}

export default function RelacionVentasSoloTotalesLayout({
  resumen = null,
  encabezadoApi = null,
  encabezadoFallback = null,
  sucursalNombre = '',
  ocultarFolios = false,
  className = '',
}) {
  const data = useMemo(() => (resumen && typeof resumen === 'object' ? resumen : null), [resumen]);
  const ocultarFoliosEfectivo = useMemo(
    () =>
      ocultarFolios === true ||
      data?.filtros?.ocultarFolios === true ||
      data?.alertas?.ocultarFolios === true,
    [ocultarFolios, data],
  );

  const header = data?.header ?? {};
  const filtros = data?.filtros ?? {};
  const agrupado = data?.agrupadoPorTurno === true;
  const turnos = useMemo(
    () => (agrupado && Array.isArray(data?.turnos) ? data.turnos : []),
    [agrupado, data?.turnos],
  );

  if (!data) {
    return (
      <div className="rv-st-report rv-st-report--empty">
        Sin datos para los filtros seleccionados.
      </div>
    );
  }

  const enc = encabezadoApi && encabezadoTieneDatos(encabezadoApi) ? encabezadoApi : null;
  const nombreEmpresa =
    String(enc?.nombreComercial ?? header.empresaNombre ?? '').trim() ||
    String(encabezadoFallback?.nombreComercial ?? '').trim();
  const domicilio =
    String(enc?.domicilioFiscal ?? header.empresaDomicilio ?? '').trim() ||
    String(encabezadoFallback?.domicilioFiscal ?? '').trim();
  const rfc = String(enc?.rfc ?? header.rfc ?? '').trim();
  const sucNombre = String(header.sucursalNombre ?? enc?.sucursalNombre ?? sucursalNombre ?? '').trim();

  return (
    <div className={`rv-st-report pop-report ${className}`.trim()}>
      <header className="pop-report-headerenc">
        <div className="pop-report-headerenc__empresa">
          {nombreEmpresa && (
            <h1 className="pop-report-headerenc__nombre">
              {nombreEmpresa.toUpperCase()}
              {sucNombre && !nombreEmpresa.toUpperCase().includes(sucNombre.toUpperCase()) && (
                <span className="pop-report-headerenc__sucursal-append"> — {sucNombre.toUpperCase()}</span>
              )}
            </h1>
          )}
          {domicilio && <p className="pop-report-headerenc__detalle rv-report-domicilio">{domicilio}</p>}
          {rfc && (
            <p className="pop-report-headerenc__detalle pop-report-headerenc__rfc">RFC: {rfc.toUpperCase()}</p>
          )}
          {header.sucursalDomicilio && (
            <p className="pop-report-headerenc__detalle rv-st-sucursal-dom">{header.sucursalDomicilio}</p>
          )}
        </div>
        <div className="pop-report-headerenc__titulos">
          <h2 className="pop-report-headerenc__reporte dc-report-title-highlight">
            {RELACION_VENTAS_SOLO_TOTALES_TITULO}
          </h2>
        </div>
        <div className="pop-report-headerenc__filtros">
          <p className="pop-report-headerenc__filtros-line">
            Fechas: {formatSoloTotalesDate(filtros.fechaInicio)} — {formatSoloTotalesDate(filtros.fechaFin)}
          </p>
          <p className="pop-report-headerenc__filtros-line">
            Cajas: {filtros.cajaInicio ?? '—'} — {filtros.cajaFin ?? filtros.cajaInicio ?? '—'}
          </p>
          <p className="pop-report-headerenc__filtros-line">
            Turnos: {filtros.turnoInicio ?? '—'} — {filtros.turnoFin ?? filtros.turnoInicio ?? '—'}
          </p>
        </div>
      </header>

      <div className="rv-st-body">
        {agrupado ? (
          turnos.length === 0 ? (
            <div className="rv-st-report--empty">Sin movimientos por turno en el rango seleccionado.</div>
          ) : (
            turnos.map((turno, index) => (
              <section key={`${turno.titulo}-${turno.turno}-${index}`} className="rv-st-turno-block">
                <header className="rv-st-turno-head">
                  <span className="rv-st-turno-head__badge">Turno</span>
                  <h3 className="rv-st-turno-head__title">
                    {turno.titulo ||
                      (turno.turnoNombre
                        ? `Turno: ${turno.turnoNombre}`
                        : turno.turno != null
                          ? `Turno: ${turno.turno}`
                          : 'Turno: —')}
                  </h3>
                </header>
                <SoloTotalesSeccionBody seccion={turno} ocultarFolios={ocultarFoliosEfectivo} />
              </section>
            ))
          )
        ) : (
          <SoloTotalesSeccionBody seccion={data} ocultarFolios={ocultarFoliosEfectivo} />
        )}
      </div>
    </div>
  );
}
